import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { Size } from '@/dap-design-system'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './avatar.scss?inline'

export type AvatarShape = 'circle' | 'rounded' | 'square'
export type AvatarVariant = 'image' | 'initials' | 'icon'

/**
 * `dap-ds-avatar`
 * @summary Avatar component can be used to display user profile images, initials, or icons.
 * @element dap-ds-avatar
 * @title - Avatar
 *
 * @slot icon - The icon to display when variant is 'icon'.
 * @slot fallback - Custom fallback content when image fails to load.
 *
 * @event dds-click - Fired when the avatar is clicked (only when interactive).
 * @event dds-load - Fired when the image loads successfully.
 * @event dds-error - Fired when the image fails to load.
 *
 * @csspart base - The main avatar container.
 * @csspart img - The avatar image.
 * @csspart initials - The initials container.
 * @csspart icon - The icon container.
 * @csspart fallback - The fallback content container.
 * @csspart loading - The loading indicator.
 *
 * @cssproperty --dds-avatar-border-radius - The border radius of the avatar. Default varies by shape.
 * @cssproperty --dds-avatar-background-color - The background color of the avatar. Default is neutral.
 * @cssproperty --dds-avatar-border-width - The width of the avatar's border. Default is 0.
 * @cssproperty --dds-avatar-border-color - The color of the avatar's border. Default is transparent.
 * @cssproperty --dds-avatar-border-style - The style of the avatar's border. Default is solid.
 * @cssproperty --dds-avatar-transition - The transition property for the avatar. Default is 'all 0.2s ease-in-out'.
 * @cssproperty --dds-avatar-text-color - The color of initials text. Default is neutral strong.
 * @cssproperty --dds-avatar-font-size - The font size for initials. Calculated based on size.
 * @cssproperty --dds-avatar-font-weight - The font weight for initials. Default is semibold.
 *
 * @cssproperty --dds-avatar-size-lg - The size of the large avatar. Default is 80px.
 * @cssproperty --dds-avatar-size-md - The size of the medium avatar. Default is 64px.
 * @cssproperty --dds-avatar-size-sm - The size of the small avatar. Default is 48px.
 * @cssproperty --dds-avatar-size-xs - The size of the extra small avatar. Default is 32px.
 * @cssproperty --dds-avatar-size-xxs - The size of the extra extra small avatar. Default is 24px.
 */
@customElement('dap-ds-avatar')
export default class DapDSAvatar extends DdsElement {
  /** The size of the avatar
   * @default md
   * @type { 'xxs' | 'xs' | 'sm' | 'md' | 'lg' }
   * @example
   * ```html
   * <dap-ds-avatar size="xxs">
   * <dap-ds-avatar size="xs">
   * <dap-ds-avatar size="sm">
   * <dap-ds-avatar size="md">
   * <dap-ds-avatar size="lg">
   * ```
   */
  @property({ reflect: true }) size: Size = 'md'
  /** The shape of the avatar
   * @default circle
   * @type { 'circle' | 'rounded' | 'square' }
   * @example
   * ```html
   * <dap-ds-avatar shape="circle">
   * <dap-ds-avatar shape="rounded">
   * <dap-ds-avatar shape="square">
   * ```
   */
  @property({ reflect: true }) shape: AvatarShape = 'circle'
  /** The variant type of the avatar
   * @default image
   * @type { 'image' | 'initials' | 'icon' }
   * @example
   * ```html
   * <dap-ds-avatar variant="image">
   * <dap-ds-avatar variant="initials">
   * <dap-ds-avatar variant="icon">
   * ```
   */
  @property({ reflect: true }) variant: AvatarVariant = 'image'
  /** The source of the avatar image */
  @property() src: string
  /** The alt text of the avatar */
  @property() alt: string
  /** The initials to display when variant is 'initials' */
  @property() initials: string
  /** Accessible label for the avatar */
  @property() label: string
  /** Loading state indicator */
  @property({ type: Boolean }) loading = false
  /** Whether the avatar is interactive (clickable) */
  @property({ type: Boolean, reflect: true }) interactive = false
  /** The width of the avatar. This will override the size */
  @property({ type: Number }) width: number
  /** The height of the avatar. This will override the size */
  @property({ type: Number }) height: number

  @state() private hasImageError = false
  @state() private isImageLoading = true

  static readonly styles = unsafeCSS(styles)

  private handleImageLoad = () => {
    this.isImageLoading = false
    this.hasImageError = false
    this.emit('dds-load')
  }

  private handleImageError = () => {
    this.isImageLoading = false
    this.hasImageError = true
    this.emit('dds-error')
  }

  private handleClick = (event: Event) => {
    if (this.interactive) {
      this.emit('dds-click', { originalEvent: event })
    }
  }

  private generateInitials(initials?: string): string {
    if (!initials || !initials.trim()) return ''
    return initials
      .trim()
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  private mapSizeToSpinnerSize(
    size: Size,
  ): 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' {
    switch (size) {
      case 'xxs':
        return 'xs'
      case 'xs':
        return 'sm'
      case 'sm':
        return 'md'
      case 'md':
        return 'lg'
      case 'lg':
        return 'xl'
      default:
        return 'lg'
    }
  }

  private renderImage() {
    if (!this.src || this.hasImageError) return nothing

    return html`
      <img
        class=${clsx('avatar__img', `avatar__img--${this.size}`)}
        style=${this.width && this.height
          ? `width: ${this.width}px; height: ${this.height}px;`
          : ''}
        part="img"
        alt=${ifDefined(this.alt)}
        src=${this.src}
        loading="lazy"
        @load=${this.handleImageLoad}
        @error=${this.handleImageError} />
    `
  }

  private renderInitials() {
    const displayInitials = this.generateInitials(this.initials)
    if (!displayInitials) return null

    return html`
      <div
        class=${clsx('avatar__initials', `avatar__initials--${this.size}`)}
        part="initials"
        aria-label=${ifDefined(this.label || `Avatar for ${this.initials}`)}>
        ${displayInitials}
      </div>
    `
  }

  private renderIcon() {
    return html`
      <div
        class=${clsx('avatar__icon', `avatar__icon--${this.size}`)}
        part="icon"
        aria-label=${ifDefined(this.label || 'Avatar icon')}>
        <slot name="icon"></slot>
      </div>
    `
  }

  private renderFallback() {
    return html`
      <div
        class=${clsx('avatar__fallback', `avatar__fallback--${this.size}`)}
        part="fallback"
        aria-label=${ifDefined(this.label || 'Avatar')}>
        <slot name="fallback">
          <dap-ds-icon-user-line></dap-ds-icon-user-line>
        </slot>
      </div>
    `
  }

  private renderLoading() {
    if (!this.loading || !this.isImageLoading) return nothing

    return html`
      <div
        class=${clsx('avatar__loading', `avatar__loading--${this.size}`)}
        part="loading"
        aria-label=${ifDefined(this.label || 'Loading avatar')}>
        <dap-ds-spinner
          size=${this.mapSizeToSpinnerSize(this.size)}
          variant="brand"></dap-ds-spinner>
      </div>
    `
  }

  private renderContent() {
    if (this.loading && this.isImageLoading && this.variant === 'image') {
      return this.renderLoading()
    }

    switch (this.variant) {
      case 'image':
        if (this.src && !this.hasImageError) {
          return this.renderImage()
        } else if (this.initials) {
          return this.renderInitials()
        } else {
          return this.renderFallback()
        }
      case 'initials':
        return this.renderInitials() || this.renderFallback()
      case 'icon':
        return this.renderIcon()
      default:
        return this.renderFallback()
    }
  }

  render() {
    const baseClasses = clsx(
      'avatar',
      `avatar--${this.size}`,
      `avatar--${this.shape}`,
      `avatar--${this.variant}`,
      {
        'avatar--interactive': this.interactive,
        'avatar--loading': this.loading && this.isImageLoading,
        'avatar--error': this.hasImageError,
      },
    )

    return html`
      <div
        class=${baseClasses}
        part="base"
        role=${ifDefined(this.interactive ? 'button' : undefined)}
        tabindex=${ifDefined(this.interactive ? 0 : undefined)}
        @click=${this.handleClick}
        @keydown=${(e: KeyboardEvent) => {
          if (this.interactive && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            this.handleClick(e)
          }
        }}>
        ${this.renderContent()}
      </div>
    `
  }
}
